/* One open Sandbox Chat, kept current by polling (no push channel).

   Fan actions return at once and the turn runs in the background on the brain, so
   the transcript is polled: every ~1s while a turn is pending or running (or the fan
   spoke last and a reply is still expected), every 2s
   while a follow-up nudge is armed, and backing off to 15s when nothing is
   happening. A poll asks only for rows after the newest one seen; a full re-read
   picks up changes to older rows (a PPV bought, a message unsent) when a turn ends
   and every so often while idle. Hidden tabs don't poll. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../../api/client';
import type { SandboxChat, SandboxMessage } from '../../../../api/client';
import { lastServerId, mergeMessages, replaceMessages, replyState } from './model';

const FAST_MS = 1000;
const FOLLOW_UP_MS = 2000;
const IDLE_MS = [3000, 5000, 8000, 15000];
const FULL_REREAD_MS = 30000;
/** After a fan action, poll fast this long even before the brain reports the turn. */
const KICK_MS = 15000;

export type ChatMeta = Omit<SandboxChat, 'messages'>;

export function useSandboxChat(accountId: string, fanId: string | null) {
  const [meta, setMeta] = useState<ChatMeta | null>(null);
  const [messages, setMessages] = useState<SandboxMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missing, setMissing] = useState(false);

  const msgsRef = useRef<SandboxMessage[]>([]);
  const gen = useRef(0);
  const timer = useRef<number | null>(null);
  const idleStep = useRef(0);
  const kickUntil = useRef(0);
  const lastFull = useRef(0);
  const wasBusy = useRef(false);
  const loaded = useRef(false);

  const setMsgs = (fn: (cur: SandboxMessage[]) => SandboxMessage[]) => {
    msgsRef.current = fn(msgsRef.current);
    setMessages(msgsRef.current);
  };

  const schedule = useCallback((ms: number, run: () => void) => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(run, ms);
  }, []);

  const poll = useCallback(async (full = false) => {
    if (!fanId) return;
    const my = gen.current;
    // The first read always runs; only follow-up polls wait for the tab to be seen.
    if (document.hidden && loaded.current) {
      schedule(3000, () => void poll());
      return;
    }
    const since = full ? undefined : lastServerId(msgsRef.current);
    try {
      const r = await api.getSandboxChat(accountId, fanId, since || undefined);
      if (my !== gen.current) return;
      loaded.current = true;
      const { messages: rows, ...rest } = r;
      setMeta(rest);
      setError(null);
      if (full || !since) {
        lastFull.current = Date.now();
        setMsgs((cur) => replaceMessages(cur, rows));
      } else {
        setMsgs((cur) => mergeMessages(cur, rows));
      }
      const busy = r.turn_in_progress;
      const endedTurn = wasBusy.current && !busy;
      wasBusy.current = busy;
      let next: number;
      // Keep polling fast while a reply is still expected, even before the brain
      // reports the turn: picking a message up can take it tens of seconds.
      if (busy || Date.now() < kickUntil.current || replyState(msgsRef.current) === 'queued') {
        next = FAST_MS;
        idleStep.current = 0;
      } else if (r.follow_up_pending) {
        next = FOLLOW_UP_MS;
        idleStep.current = 0;
      } else {
        next = IDLE_MS[Math.min(idleStep.current, IDLE_MS.length - 1)] ?? 15000;
        idleStep.current += 1;
      }
      const reread = endedTurn || (!busy && Date.now() - lastFull.current > FULL_REREAD_MS);
      schedule(endedTurn ? 400 : next, () => void poll(reread));
    } catch (e) {
      if (my !== gen.current) return;
      const msg = e instanceof Error ? e.message : String(e);
      if (/no (such )?sandbox chat/i.test(msg)) {
        setMissing(true);
        return;
      }
      setError(msg);
      schedule(5000, () => void poll());
    } finally {
      if (my === gen.current) setLoading(false);
    }
  }, [accountId, fanId, schedule]);

  useEffect(() => {
    gen.current += 1;
    msgsRef.current = [];
    setMessages([]);
    setMeta(null);
    setError(null);
    setMissing(false);
    wasBusy.current = false;
    loaded.current = false;
    idleStep.current = 0;
    if (!fanId) return undefined;
    setLoading(true);
    void poll(true);
    const onVisible = () => { if (!document.hidden) void poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      gen.current += 1;
      document.removeEventListener('visibilitychange', onVisible);
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [fanId, poll]);

  /** A fan action was accepted: poll fast until the turn shows up and finishes. */
  const kick = useCallback((full = false) => {
    kickUntil.current = Date.now() + KICK_MS;
    idleStep.current = 0;
    schedule(250, () => void poll(full));
  }, [poll, schedule]);

  return { meta, setMeta, messages, setMsgs, loading, error, missing, kick };
}
